import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "src/components/dashboard";
import { Input } from "src/components/inputs";
import { Table } from "src/components/ui";
import { formatDate2 } from "src/utils";
import { BsArrowLeft } from "react-icons/bs";
import searchIcon from "src/assets/images/input/searchIcon.svg";
import addIcon from "src/assets/images/common/add.svg";
import { PaginationProps } from "src/components/ui/Pagination";
import { CategoryAnalytics } from "src/components/partners";
import { useLoading } from "src/hooks";
import { useLazyDumpPartnersDataQuery } from "src/store/services/artisanApi";
import { toast, ToastContainer } from "react-toastify";
import { auth } from "src/store/Auth";
import { theme } from "src/styles/Theme";
import { useGetAdminQuery } from "src/store";
import AppModal from "src/components/ui/widget/Modal/Modal";
import ModalContent from "src/components/common/ModalContent";
import PartInput from "src/components/inputs/partsearch";
import {
  Partner as PartnerType,
  useGetCentralPartnerCategoryByIdQuery,
  useRemoveCentralPartnerCategoryMutation
} from "src/store/services/partnerApi";
import { usePagination } from "src/hooks/usePagination";
import {
  Button,
  ButtonClass,
  ConfirmationModal,
  Flex,
} from "src/components/ui";
import { SendNotificationModal } from "src/components/users";
import exportImg from "../../assets/images/modal/Icon.png";
import notifyallcat from "../../assets/images/profile/Partner/notify.png";

/* ---------------- Icons ---------------- */

const MoreIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="4" r="1.5" fill="#6b6675" />
    <circle cx="10" cy="10" r="1.5" fill="#6b6675" />
    <circle cx="10" cy="16" r="1.5" fill="#6b6675" />
  </svg>
);

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M0.666626 8C0.666626 8 3.33329 2.66667 7.99996 2.66667C12.6666 2.66667 15.3333 8 15.3333 8C15.3333 8 12.6666 13.3333 7.99996 13.3333C3.33329 13.3333 0.666626 8 0.666626 8Z"
      stroke="#6b6675"
      strokeWidth="1.5"
    />
    <path
      d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z"
      stroke="#6b6675"
      strokeWidth="1.5"
    />
  </svg>
);

/* ---------------- Action Dropdown ---------------- */

const ActionDropdown = ({
  partnerId,
  onViewDetails,
}: {
  partnerId: string;
  onViewDetails: (id: string) => void;
}) => {
  const [open, setOpen] = useState(false);

  const handleClick = () => setOpen(!open);

  const handleView = () => {
    onViewDetails(partnerId);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button onClick={handleClick}>
        <MoreIcon />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-40 bg-white shadow rounded z-10">
          <button
            className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 w-full"
            onClick={handleView}
          >
            <EyeIcon /> View Details
          </button>
        </div>
      )}
    </div>
  );
};

/* ---------------- Main Page ---------------- */

const CentralCategoryPartners = () => {
  const { categoryName } = useParams<{ categoryName: string }>();
  const { partnerIdNo } = useParams();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const navigate = useNavigate();
  const [isOccupManager, setIsOccupManager] = useState(false);
  const { authUser } = auth.use();
  const [triggerDumpArtisansData] = useLazyDumpPartnersDataQuery();
  const [showExportModal, setShowExportModal] = useState(false);
  const [adminName, setAdminName] = useState("");

  useEffect(() => {
    if (authUser?.role?.includes("occupationalManager")) {
      setIsOccupManager(true);
    }
  }, [authUser]);

  const [showImportant, setShowImportant] = useState(true);
  useEffect(() => {
    if (authUser?.role?.includes("centralPartnerManager")) {
      setShowImportant(false);
    }
  }, [authUser]);

  const [paginationData, setPaginationData] = useState<PaginationProps>({
    page: 0,
    pageSize: 0,
    total: 0,
    totalPages: 0,
  });

  const { data: adminData, isLoading: adminLoading } = useGetAdminQuery(
    categoryName!,
    {
      skip: !categoryName,
    },
  );

  const [removeCentralPartnerCategory] =
  useRemoveCentralPartnerCategoryMutation();

  useEffect(() => {
    if (adminData?.payload?.data?.admin) {
      const admin = adminData.payload.data.admin;
      setAdminName(admin.first_name + " " + admin.last_name);
    }
  }, [adminData]);

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Export partners data");
    setShowExportModal(!showExportModal);
  };

  // State
  const [pPage, setPage] = useState(1);
  const [search, setSearch] = useState("");

  // Fetch partners from API
  const { data, isLoading } = useGetCentralPartnerCategoryByIdQuery({
    id: categoryName || "",
    page: pPage,
    limit: 10,
  });

  const partners = data?.payload?.outlets || [];
  const category_name = data?.payload?.category?.name || [];
  const meta = data?.payload?.pagination;

  // export interface PaginationProps {
  //   page: number;
  //   pageSize: number;
  //   total: number;
  //   totalPages: number;
  // }

  useEffect(() => {
    if (meta) {
      setPaginationData({
        page: meta.page,
        pageSize: meta.limit, // map limit → pageSize
        total: meta.total,
        totalPages: meta.totalPages,
      });
    }

    console.log(meta);

    console.log(paginationData);
  }, [data]);
  console.log(paginationData);

  // Filter partners based on search
  const filteredPartners = useMemo(() => {
    if (!search.trim()) return partners;
    const q = search.toLowerCase();
    return partners.filter(
      (p) =>
        p.business_name.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q) ||
        p.phone.toLowerCase().includes(q) ||
        p.country.toLowerCase().includes(q),
    );
  }, [partners, search]);

  // Pagination
  const { page, limit, Pagination } = usePagination(
    {
      page: pPage,
      limit: 3,
      total: filteredPartners.length,
      setPageNo: setPage,
    },
    paginationData,
  );

  console.log(page);

  const {
    loading: exportingData,
    startLoading: startExportingData,
    stopLoading: stopExportingData,
  } = useLoading(false);

//   const handleDeletePartner = async (id: string) => {
//   try {
//     await useRemoveCentralPartnerCategoryMutation({id});

//     // optional: success feedback
//     toast.success("Partner deleted successfully");

//     // optional: refetch or update UI
//     // refetchPartners();
//   } catch (error) {
//     toast.error("Failed to delete partner");
//   }
// };

const handleDeletePartner = async (id: string) => {
  try {
    await removeCentralPartnerCategory({
      outlets: [id],
    });

    toast.success("Partner deleted successfully");

    // optional refresh
    // refetchPartners();
  } catch (error) {
    toast.error("Failed to delete partner");
  }
};

  const dumpArtisanData = async () => {
    startExportingData();
    try {
      const res: any = await triggerDumpArtisansData().unwrap();
      setShowExportModal(false);
      toast.success(res?.message);
      const url = res?.payload?.data?.fileUrl;
      if (url) window.location.href = url;
    } catch (err: any) {
      toast.error(err?.data?.error?.message || "Export failed");
    } finally {
      stopExportingData();
    }
  };

  // Table headers
  const headers = [
    {
      title: "Logo",
      render: (r: PartnerType) => (
        <img src={r.display_picture} className="w-10 h-10 rounded-full" />
      ),
    },
    { title: "Business Name", render: (r: PartnerType) => r.business_name },
    { title: "Email", render: (r: PartnerType) => r.email },
    { title: "Phone", render: (r: PartnerType) => r.phone },
    { title: "Country", render: (r: PartnerType) => r.country },
    {
      title: "Created At",
      render: (r: PartnerType) => formatDate2(r.createdAt),
    },
    {
      title: "Status",
      render: (r: PartnerType) => (
        <span className="px-3 py-1 rounded-full text-xs bg-purple-600 text-white">
          {r.status}
        </span>
      ),
    },
    {
      title: "Actions",
      align: "right",
      render: (r: PartnerType) => (
        <div className={`flex items-center justify-end gap-2 ${showImportant ? "" : "hidden"}`}>
          {/* View Button */}
          {/* <button
            onClick={() => navigate(`/partners/details/${r._id}`)}
            className="px-3 py-1 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            View
          </button> */}

          {/* Delete Button */}
          <button
            onClick={() => handleDeletePartner(r._id)}
            className="px-3 py-1 text-sm text-white bg-red-900 rounded-md hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout pageTitle="">
      <SendNotificationModal
        open={open}
        userId={[""]}
        handleClose={handleClose}
        artisan={"partner"}
      />

      <div className="p-6 bg-[#f5f7fa] min-h-screen">
        {/* Back Button */}
        <button
          onClick={() => navigate("/centralmanger")}
          className={`flex items-center gap-2 text-purple-600 mb-4 ${showImportant ? "" : "hidden"}`}
        >
          <BsArrowLeft /> Back to Total Partners
        </button>
        {/* Search + Add */}
        <div className="flex justify-between mb-6 w-full">
          <div className="flex justify-start gap-4">
            <div className="flex flex-col justify-start">
              <div
                style={{ fontWeight: "600" }}
                className="text-[32px] text-[#3a3541]"
              >
                {adminName}
              </div>
              {/* <div className="text-[#45556C]">
                {meta?.total} {category_name}
              </div> */}
            </div>
          </div>
          <div className="flex justify-center gap-5">
            <PartInput
              type="search"
              icon={<img src={searchIcon} />}
              placeholder={"Search " + category_name + "..."}
              handleChange={(e) => setSearch(e.target.value)}
            />

            {/* <Button
              onClick={handleExport}
              classes={[ButtonClass.SOLID, ButtonClass.WITH_ICON]}
              style={{
                backgroundColor: theme.colors.white,
                height: "48px",
                color: "black",
                minWidth: "145px",
                border: "1.33px solid #0000001A",
              }}
              className={`${isOccupManager ? "!hidden" : ""}`}
            >
              <img src={exportImg} alt="" />
              <span>Export</span>
            </Button>

            <Button
              onClick={handleOpen}
              classes={[ButtonClass.SOLID, ButtonClass.WITH_ICON]}
              style={{
                backgroundColor: theme.colors.purple,
                height: "48px",
                width: "max-content",
              }}
              className={`${isOccupManager ? "!hidden" : ""}`}
            >
            <img src={notifyallcat} alt="" />
              <span>Notify all</span>
            </Button> */}
            <button
              onClick={() => navigate(`/partners/group/add/${categoryName}`)}
              className={`bg-purple-600 text-white px-4 py-2 rounded flex items-center h-12 ${showImportant ? "" : "hidden"}`}
            >
              <img src={addIcon} className="inline mr-2" /> Add New
            </button>
          </div>
        </div>
        <CategoryAnalytics partner_id={categoryName as string} />
        {/* Table */}
        <div className="bg-white rounded shadow">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">Loading...</div>
          ) : filteredPartners.length > 0 ? (
            <Table
              rows={filteredPartners}
              headers={headers}
              showHead
              allowRowClick
              onRowClick={(r: PartnerType) =>
                navigate(`/partners/details/${r._id}`)
              }
            />
          ) : (
            <div className="p-4 text-center text-gray-500">
              No partners found
            </div>
          )}
        </div>

        <AppModal
          open={showExportModal}
          onClose={() => setShowExportModal(false)}
          content={
            <ModalContent
              content1="Export Partners Data"
              content2="Are you sure you want to export patners data?"
              btnAction={dumpArtisanData}
              linkContent={"Yes, Please"}
              onClick={() => setShowExportModal(false)}
              loading={exportingData}
            />
          }
        />

        {/* Pagination */}
        <div className="mt-6">
          <Pagination />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CentralCategoryPartners;
